import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Check, ClockIcon, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface CheckStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isGivenCheck: boolean;
  checkId: string;
  checkAmount: number;
  checkDescription: string;
  onStatusUpdate: (status: 'alindi' | 'ertelendi' | 'alinamadi', newDueDate?: Date) => Promise<void>;
}

const CheckStatusDialog: React.FC<CheckStatusDialogProps> = ({
  open,
  onOpenChange,
  isGivenCheck,
  checkId,
  checkAmount,
  checkDescription,
  onStatusUpdate,
}) => {
  const { t } = useTranslation();
  const [newDueDate, setNewDueDate] = useState<Date | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<'alindi' | 'ertelendi' | 'alinamadi' | null>(null);

  const handleStatusChange = async () => {
    if (!selectedOption) return;

    setLoading(true);
    try {
      if (selectedOption === 'ertelendi' && !newDueDate) {
        throw new Error(t('check_status.postpone_date_required'));
      }

      await onStatusUpdate(selectedOption, newDueDate);
      onOpenChange(false);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const resetDialog = () => {
    setSelectedOption(null);
    setNewDueDate(undefined);
    setCalendarOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetDialog();
    }
    onOpenChange(open);
  };

  const checkTypeText = isGivenCheck ? t('check_status.given_check_title') : t('check_status.received_check_title');
  const questionText = isGivenCheck
    ? t('check_status.given_check_question')
    : t('check_status.received_check_question');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{checkTypeText}</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {questionText}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="mb-4 p-3 bg-primary/10 rounded-lg">
            <p className="font-medium">{checkDescription}</p>
            <p className="text-lg font-bold">₺{checkAmount.toLocaleString()}</p>
          </div>

          <div className="space-y-3">
            <Button
              type="button"
              variant={selectedOption === 'alindi' ? "default" : "outline"}
              className={cn(
                "w-full justify-start text-left font-normal",
                selectedOption === 'alindi' ? "border-green-500 bg-green-500/10" : ""
              )}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedOption('alindi');
              }}
            >
              <Check className="mr-2 h-4 w-4 text-green-500" />
              {isGivenCheck ? t('check_status.paid') : t('check_status.collected')}
            </Button>

            <div className="space-y-2">
              <Button
                type="button"
                variant={selectedOption === 'ertelendi' ? "default" : "outline"}
                className={cn(
                  "w-full justify-start text-left font-normal",
                  selectedOption === 'ertelendi' ? "border-amber-500 bg-amber-500/10" : ""
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedOption('ertelendi');
                }}
              >
                <ClockIcon className="mr-2 h-4 w-4 text-amber-500" />
                {t('check_status.postponed')}
              </Button>

              {selectedOption === 'ertelendi' && (
                <div className="pl-6">
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !newDueDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newDueDate ? format(newDueDate, "PPP", { locale: tr }) : t('check_status.select_new_due_date')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={newDueDate}
                        onSelect={(date) => {
                          setNewDueDate(date);
                          setCalendarOpen(false);
                        }}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>

            <Button
              type="button"
              variant={selectedOption === 'alinamadi' ? "default" : "outline"}
              className={cn(
                "w-full justify-start text-left font-normal",
                selectedOption === 'alinamadi' ? "border-red-500 bg-red-500/10" : ""
              )}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedOption('alinamadi');
              }}
            >
              <AlertTriangle className="mr-2 h-4 w-4 text-red-500" />
              {isGivenCheck ? t('check_status.could_not_pay') : t('check_status.could_not_collect')}
            </Button>
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <Button
            type="button"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onOpenChange(false);
            }}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleStatusChange();
            }}
            disabled={!selectedOption || (selectedOption === 'ertelendi' && !newDueDate) || loading}
            className={loading ? "opacity-70" : ""}
          >
            {loading ? t('check_status.processing') : t('common.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CheckStatusDialog; 